/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import type {
  PluginManager,
  PluginVisibility,
  ScopeName,
} from '@opengeoweb/plugin-interface';
import type { ConfigType } from '@opengeoweb/shared';
import type { CoreAppStore } from '@opengeoweb/store';
import {
  genericActions,
  genericSelectors,
  mapActions,
  syncConstants,
} from '@opengeoweb/store';
import { usePluginToRedux } from './usePluginToRedux';
import { useReduxToPlugin } from './useReduxToPlugin';
import type { StoreWithPlugins } from '../../store';
import { getOverlayPlugins } from '../../store';

interface UsePluginReduxBridgeOptions {
  pluginBridge: PluginManager;
  sourceId: string;
  pluginId?: string;
  scopes?: string[];
  applicationConfig?: ConfigType;
  hostPanelId?: string;
}

export const usePluginReduxBridge = ({
  pluginBridge,
  sourceId,
  pluginId,
  scopes,
  applicationConfig,
  hostPanelId,
}: UsePluginReduxBridgeOptions): void => {
  const dispatch = useDispatch();

  const hasScope = useCallback(
    (scope: ScopeName): boolean => (!scopes ? true : scopes.includes(scope)),
    [scopes],
  );

  // Resolve which map(s) this plugin's features should render on.
  // - A view plugin has no host: it is its own panel, linked to maps by the
  //   workspace preset, so there is nothing to resolve here ([]).
  // - An overlay plugin renders on its host panel's map(s): if the host is
  //   itself a Map (mapId === panelId) it renders on that map; if the host is a
  //   view/viewplugin, it renders on the map(s) that host is linked to.
  const hostMapIds = useSelector((state: CoreAppStore): string[] => {
    if (!hostPanelId) {
      return [];
    }
    if ((state.webmap?.allIds ?? []).includes(hostPanelId)) {
      return [hostPanelId];
    }
    return genericSelectors.getLinkedMapIds(state, hostPanelId);
  }, shallowEqual);

  const overlayVisibility = useSelector(
    (state: StoreWithPlugins): PluginVisibility | undefined => {
      if (!hostPanelId || !pluginId) {
        return undefined;
      }
      const overlayPlugin = getOverlayPlugins(state, hostPanelId).find(
        (plugin) => plugin.pluginId === pluginId,
      );
      if (!overlayPlugin) {
        return undefined;
      }
      return overlayPlugin.isVisible ? 'visible' : 'hidden';
    },
  );

  useEffect(() => {
    if (hasScope('time')) {
      dispatch(
        genericActions.syncGroupAddSource({
          id: sourceId,
          type: [syncConstants.SYNCGROUPS_TYPE_SETTIME],
        }),
      );
    }
    if (hasScope('features')) {
      dispatch(
        genericActions.syncGroupAddSource({
          id: sourceId,
          type: [syncConstants.SYNCGROUPS_TYPE_SETFEATURES],
        }),
      );
      // Temporarily set this to true, but should be handled in the plugin-interface
      // https://gitlab.com/opengeoweb/plugin-development/plugin-interface/-/work_items/52
      dispatch(
        mapActions.setDisableMapPin({ mapId: sourceId, disableMapPin: true }),
      );
    }

    return (): void => {
      dispatch(genericActions.syncGroupRemoveSource({ id: sourceId }));
    };
  }, [sourceId, hasScope, dispatch]);

  // Overlay plugins are not part of the workspace's static links, so link their
  // feature source to the host panel's map(s) and keep it in sync as those
  // change.
  useEffect(() => {
    if (!hostPanelId || !hasScope('features')) {
      return;
    }
    dispatch(
      genericActions.updateLinkedMap({ panelId: sourceId, mapIds: hostMapIds }),
    );
  }, [dispatch, hostPanelId, sourceId, hostMapIds, hasScope]);

  useReduxToPlugin({
    pluginBridge,
    sourceId,
    hasScope,
    applicationConfig,
    overlayVisibility,
  });
  usePluginToRedux({
    pluginBridge,
    sourceId,
    hasScope,
    hostPanelId,
    pluginId,
  });
};
